

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewDepartureDateModal from 'components/NewDepartureDateModal';
import { formatDate } from 'utils/DateTimeConversion';

const DepartureDates = () => {
    const [departures, setDepartures] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('departure_dates');

    useEffect(() => {
        fetchDepartures();
    }, []);

    const fetchDepartures = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/departure-dates/');
            setDepartures(response.data);
        } catch (error) {
            console.error('Error fetching departures:', error);
            setSnackbar({ open: true, message: 'Error loading departure dates', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveDeparture = async (payload) => {
        try {
            await axiosInstance.post('/departure-dates/', payload);
            setSnackbar({ open: true, message: 'Departure Date created successfully', severity: 'success' });
            fetchDepartures();
        } catch (error) {
            console.error('Error saving departure date:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/departure-dates/${rowData.id}`);
    };

    const columns = [
        {
            Header: "DEP ID",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Trip",
            accessor: "trip_id",
            width: "20%",
            Cell: ({ row }) => row.original.trip?.name || row.original.trip_id
        },
        {
            Header: "Departure Date",
            accessor: "departure_date",
            width: "15%",
            Cell: ({ value }) => formatDate(value, 'date')
        },
        { Header: "City", accessor: "departure_city", width: "15%" },
        { Header: "Total", accessor: "total_allocation", width: "10%" },
        { Header: "Hold", accessor: "hold", width: "10%" },
        { Header: "Sold", accessor: "sold", width: "10%" },
        { Header: "Available", accessor: "available", width: "10%" },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: departures,
    }), [departures]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Departure Dates</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Departure
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : departures.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Departure Date" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewDepartureDateModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveDeparture}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default DepartureDates;
